import styled, { useTheme } from 'styled-components'
import { ListProductFilter } from '@/components/common/list'
import { TypoVariant } from '@/components/common/typography'
import { ListHeader } from '@/components/domains/layouts/header/ListHeader'
import SeoHeader from '@/components/domains/layouts/header/SeoHeader'
import { NewProductCard } from '@/components/domains/products/new-product-card/new-product-card'
import {
  PRODUCT_CARD_SIZES,
  THUMBNAIL_STATES,
} from '@/components/domains/products/new-product-card/new-product-card.type'
import SkeletonProduct from '@/components/domains/products/skeleton/SkeletonProduct'
import { LocationEnum } from '@/constants/location.enum'
import { ProductSkeletonLengthEnum } from '@/constants/product-skeleton-length.enum'
import { ProductThumbnailSizeEnum } from '@/constants/product-thumbnail-size.enum'
import { useHomeMoreListTracker } from '@/containers/event-tracker/home-more-list.event-tracker'
import { useCustomRouter } from '@/containers/hooks'
import useDisplayCollectionProductListV3 from '@/containers/hooks/display-collection/display-collection-product-list-v3.container.hook'
import { AdPetType, DisplayCollectionCollectionType } from '@/graphql/generated/schemas'
import { ProductTypeDefinedByFrontend } from '@/types/product-type-defined-by.frontend'
import { transformProductTypeDefinedByFrontend } from '@/utils/product/product-card.util'
import { percentText } from '@/utils/utilNumber'
import { getSeoKeywordByPetType } from '@/utils/utilSeo'

interface PageSection {
  petType: AdPetType
}

export const ListDisplayCollectionV3 = ({ petType: ssrPetType }: PageSection) => {
  const { color } = useTheme()
  const { query, asPath } = useCustomRouter()
  const displayCollectionId = query?.displayCollectionId as string
  const { petType } = getSeoKeywordByPetType(ssrPetType)

  const { isLoading, orderBy, ref, displayCollectionData, title, totalCount, type } = useDisplayCollectionProductListV3(
    {
      displayCollectionId,
      exposeHiddenProduct: !!query?.isSecret,
    }
  )
  const { trackByDisplayCollectionType } = useHomeMoreListTracker()

  const handleClickProduct = (product: ProductTypeDefinedByFrontend) => {
    trackByDisplayCollectionType({
      product,
      displayCollectionType: type as DisplayCollectionCollectionType,
      title,
      displayCollectionId,
      location: LocationEnum.List,
    })
  }

  return (
    <>
      <SeoHeader
        pathname={asPath}
        title={`${title || query?.title} | ${petType}  - 핏펫몰`}
        metaDescription={`${petType}를 위한 ${title || query?.title}} 제품이 한 자리에 다 모였습니다! 핏펫이 자랑하는 ${
          title || query?.title
        }의 다양한 제품들을 지금 바로 만나보세요.`}
      />
      <ListHeader title={title} />
      <StyledContainer>
        <ListProductFilter orderBy={orderBy} totalCount={totalCount} />
        <StyledListLayout>
          {displayCollectionData?.pages
            ?.flatMap((page) => page.data?.products)
            ?.map((product, index) => {
              if (!product) {
                return null
              }

              const productCard = transformProductTypeDefinedByFrontend(product)

              const label =
                type === DisplayCollectionCollectionType.TodaySpecialPrice && productCard.hasDiscount
                  ? {
                      value: percentText(productCard.discountRate),
                      fontColor: color.grayWhite,
                      backgroundColor: color.red500,
                      variant: TypoVariant.Body5Bold,
                    }
                  : undefined

              return (
                <StyledProductCardContainer key={product?.id} ref={ref}>
                  <NewProductCard
                    product={productCard}
                    size={PRODUCT_CARD_SIZES.half}
                    thumbnailState={THUMBNAIL_STATES.basicLabel}
                    label={label}
                    rankingNumber={index}
                    hasCartButton
                    onClickProductCard={() => handleClickProduct(productCard)}
                  />
                </StyledProductCardContainer>
              )
            })}
        </StyledListLayout>
        <StyledSkeleton>
          {isLoading && (
            <SkeletonProduct length={ProductSkeletonLengthEnum.Eight} size={ProductThumbnailSizeEnum.Fill} />
          )}
        </StyledSkeleton>
      </StyledContainer>
    </>
  )
}

const StyledContainer = styled.div`
  margin: 1.6rem;
`
const StyledListLayout = styled.div`
  display: grid;
  grid-auto-flow: row;
  grid-template-columns: repeat(2, 1fr);
  grid-row-gap: 3.2rem;
  grid-column-gap: 0.8rem;
`

const StyledSkeleton = styled(StyledListLayout)`
  & > div {
    &:nth-child(1),
    &:nth-child(2) {
      margin-top: 0;
    }
  }
`

const StyledProductCardContainer = styled.div`
  position: relative;
`
